import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { Link } from "@tanstack/react-router";
import { TermSetImportFlow } from "@/components/TermSetImportFlow";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { ErrorBox, PageHeader } from "@/components/common";
import { Button, Checkbox, Tabs } from "@/components/ui-kit";
import { useTermSets, type TermSetEntry } from "@/components/TermSetSelector";

type Claim = { id: string; kind: string; normalized_claim: { name?: string }; source_locator: string };
type DraftResult = { version_id: string; entities_created: number; terms_attached?: number; status: string };
type Detail = Omit<TermSetEntry, "versions"> & { versions: Array<TermSetEntry["versions"][number] & { entries: Array<{
  proposal_id: string; source_label: string; source_description: string; canonical_key: string;
}> }> };
const TAGS = ["lob", "product", "business_unit", "jurisdiction", "language"] as const;
type TermTab = "terms" | "versions" | "import" | "settings";
const TERM_TABS: Array<{ value: TermTab; label: string }> = [
  { value: "terms", label: "Terms" }, { value: "versions", label: "Versions" },
  { value: "import", label: "Import" }, { value: "settings", label: "Settings" },
];

export function TermSets() {
  const ws = useApp((state) => state.workspaceId);
  const { canEdit, canAdmin } = useWorkspacePermissions();
  const qc = useQueryClient();
  const library = useTermSets(ws);
  const openedWorkspace = useRef<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [tags, setTags] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const [confirm, setConfirm] = useState("");
  const [draftResult, setDraftResult] = useState<DraftResult | null>(null);
  const [activeTab, setActiveTab] = useState<TermTab>("import");
  const detail = useQuery<Detail>({
    queryKey: ["term-set-detail", ws, activeId],
    queryFn: () => rawFetch(`/ontology/term-sets/${activeId}/?workspace=${encodeURIComponent(ws!)}`),
    enabled: Boolean(ws && activeId),
  });
  const approved = useQuery<{ count: number; results: Claim[] }>({
    queryKey: ["approved-claims-for-term-set", ws],
    queryFn: async () => {
      const results: Claim[] = [];
      let count = 0;
      do {
        const page = await rawFetch<{ count: number; results: Claim[] }>(
          `/catalog/discovery/proposals/?workspace=${encodeURIComponent(ws!)}&status=approved&limit=100&offset=${results.length}`,
        );
        count = page.count;
        results.push(...page.results);
        if (!page.results.length) break;
      } while (results.length < count && results.length < 10_000);
      return { count, results };
    },
    enabled: Boolean(ws && canEdit),
  });
  const mappingSummary = useQuery<{ terms: number; matched: number; new: number }>({
    queryKey: ["mapping-summary", ws, selected],
    queryFn: () => rawFetch("/catalog/discovery/mappings/summary/", {
      method: "POST", body: JSON.stringify({ workspace: ws, proposal_ids: selected }),
    }),
    enabled: Boolean(ws && selected.length > 0 && selected.length <= 500),
  });
  const active = detail.data;
  useEffect(() => {
    if (!ws || !library.data || openedWorkspace.current === ws) return;
    openedWorkspace.current = ws;
    const first = library.data.results[0];
    setActiveId(first?.id ?? null);
    setName(first?.name ?? "");
    setActiveTab(first ? "terms" : "import");
    const current = first?.versions[0]?.tags ?? {};
    setTags(Object.fromEntries(TAGS.map((key) => [key, (current[key] ?? []).join(", ")])));
  }, [ws, library.data]);
  useEffect(() => {
    if (active && active.id === activeId) {
      setSelected(active.versions[0]?.entries.map((entry) => entry.proposal_id) ?? []);
    }
  }, [activeId, active?.latest_version_id]);

  async function act(task: () => Promise<unknown>, success: string) {
    setBusy(true); setError(null); setMessage("");
    try {
      await task();
      setMessage(success);
      await qc.invalidateQueries({ queryKey: ["term-sets", ws] });
      await qc.invalidateQueries({ queryKey: ["term-set-detail", ws] });
      await qc.invalidateQueries({ queryKey: ["approved-claims-for-term-set", ws] });
    } catch (caught) { setError(caught as Error); }
    finally { setBusy(false); }
  }

  function choose(item: TermSetEntry | null) {
    setActiveId(item?.id ?? null); setSelected([]); setName(item?.name ?? ""); setConfirm("");
    setActiveTab(item ? "terms" : "import");
    const current = item?.versions[0]?.tags ?? {};
    setTags(Object.fromEntries(TAGS.map((key) => [key, (current[key] ?? []).join(", ")])));
  }

  async function saveVersion() {
    if (!ws || !selected.length) return;
    const payload = { workspace: ws, name, proposal_ids: selected,
      tags: Object.fromEntries(TAGS.map((key) => [key, (tags[key] ?? "").split(",").map((value) => value.trim()).filter(Boolean)])) };
    await act(async () => {
      const result = active
        ? await rawFetch<Detail>(`/ontology/term-sets/${active.id}/versions/`, { method: "POST", body: JSON.stringify({
            ...payload, expected_latest_version_id: active.latest_version_id,
          }) })
        : await rawFetch<Detail>("/ontology/term-sets/", { method: "POST", body: JSON.stringify(payload) });
      setActiveId(result.id); setSelected([]);
    }, active ? "New immutable version saved." : "Term set created from approved claims.");
  }

  async function createDraft() {
    if (!ws || !active?.latest_version_id) return;
    await act(async () => {
      const result = await rawFetch<DraftResult>("/ontology/generate/", {
        method: "POST", body: JSON.stringify({
          workspace: ws, term_set_version_ids: [active.latest_version_id], model: "stub",
        }),
      });
      setDraftResult(result);
    }, "Review-required ontology draft created from this pinned term-set version.");
  }

  async function remove() {
    if (!active || !ws) return;
    await act(async () => {
      await rawFetch(`/ontology/term-sets/${active.id}/delete/`, { method: "POST", body: JSON.stringify({
        workspace: ws, confirm_name: confirm,
      }) });
      choose(null);
    }, "Term set hidden from the library; version lineage remains available.");
  }

  return <div className="space-y-5">
    <PageHeader title="Term-set library" subtitle="Versioned, reviewed vocabulary for deterministic ontology drafts." />
    <p className="text-xs text-muted">Catalog collects and reviews source terms. A term set is the approved, reusable output for an ontology draft.</p>
    {!ws && <p className="card text-sm text-muted">Select a workspace first.</p>}
    {ws && <>
      <div className="grid gap-4 lg:grid-cols-[minmax(15rem,0.8fr)_minmax(0,2fr)]">
        <section className="card space-y-3" aria-label="Term sets">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Term sets</h2>
            {canEdit && <Button onClick={() => choose(null)}>New term set</Button>}
          </div>
          {library.isLoading && <p className="text-xs text-muted">Loading library…</p>}
          {library.isError && <ErrorBox error={library.error} />}
          {library.data?.results.length === 0 && <p className="text-xs text-muted">No term sets yet. Import a synthetic dictionary or select approved Catalog claims.</p>}
          {library.data?.results.map((item) => <button key={item.id} type="button"
            className={`w-full border p-3 text-left text-sm focus-visible:outline-2 focus-visible:outline-accent ${activeId === item.id ? "border-accent" : "border-border"}`}
            onClick={() => choose(item)} aria-pressed={activeId === item.id}>
            <strong>{item.name}</strong><span className="block text-xs text-muted">v{item.versions[0]?.number} · {item.versions[0]?.entry_count} terms · {item.versions.length} version(s)</span>
            <span className="mt-1 block text-xs text-muted">Used by: {item.versions[0]?.used_by?.drafts.length ?? 0} drafts, {item.versions[0]?.used_by?.profiles.length ?? 0} retrieval profiles</span>
          </button>)}
        </section>
        <div className="min-w-0 space-y-4">
          <section className="card space-y-3" aria-label="Term-set version">
            <h2 className="font-semibold">{active ? active.name : "New term set"}</h2>
            <Tabs value={activeTab} items={TERM_TABS} onChange={setActiveTab} label="Term-set sections" />
            {detail.isLoading && activeId && <p className="text-xs text-muted">Loading version lineage…</p>}
            {detail.isError && <ErrorBox error={detail.error} />}
            {activeTab === "import" && canEdit && <TermSetImportFlow workspaceId={ws} canAdmin={canAdmin}
              onSaved={(saved) => { setActiveId(saved.id); setName(""); setActiveTab("terms");
                void qc.invalidateQueries({ queryKey: ["term-sets", ws] });
                void qc.invalidateQueries({ queryKey: ["term-set-detail", ws] }); }} />}
            {activeTab === "import" && !canEdit && <p className="text-xs text-muted">An editor can import a dictionary for review.</p>}
            {activeTab === "terms" && active && <>
              <p className="text-xs text-muted">Latest approved version: {active.versions[0]?.entry_count ?? 0} terms.</p>
              <div className="max-h-[36rem] overflow-auto border border-border"><table className="w-full text-xs"><thead><tr><th className="p-2 text-left">Term</th><th className="p-2 text-left">Definition</th></tr></thead><tbody>
                {active.versions[0]?.entries?.map((entry) => <tr key={entry.proposal_id} className="border-t border-border"><td className="p-2 font-medium">{entry.source_label}</td><td className="p-2">{entry.source_description}</td></tr>)}
              </tbody></table></div>
              {active.latest_version_id && canEdit && <Button variant="primary" disabled={busy} onClick={createDraft}>Create ontology draft from this term set</Button>}
              {draftResult && <p role="status" className="text-xs text-good">Draft created: {draftResult.entities_created} classes, {draftResult.terms_attached ?? 0} grouped terms. Review it in <Link to="/ontology" className="underline">Ontology</Link> or <Link to="/studio/composer" className="underline">Composer</Link>.</p>}
            </>}
            {activeTab === "terms" && !active && <p className="text-xs text-muted">Select a term set, or use Import to create one.</p>}
            {activeTab === "versions" && active?.versions.map((version) => <details key={version.id} className="border border-border p-2 text-xs">
              <summary className="cursor-pointer">Version {version.number} · {version.entry_count} approved claims</summary>
              <p className="mt-2 text-muted">Used by: {version.used_by?.drafts.join(", ") || "no drafts"}; {version.used_by?.profiles.join(", ") || "no retrieval profiles"}.</p>
              <ul className="mt-2 space-y-1">{version.entries?.map((entry) => <li key={entry.proposal_id}><strong>{entry.source_label}</strong> · {entry.source_description}</li>)}</ul>
            </details>)}
            {activeTab === "versions" && canEdit && <>
              {!active && <label className="block text-xs">Name<input className="input mt-1" value={name} onChange={(event) => setName(event.target.value)} maxLength={160} /></label>}
              <div className="grid gap-2 sm:grid-cols-2">{TAGS.map((key) => <label className="text-xs" key={key}>{key.replace("_", " ")}
                <input className="input mt-1" value={tags[key] ?? ""} onChange={(event) => setTags({ ...tags, [key]: event.target.value })} placeholder="Comma-separated" /></label>)}</div>
              <div><h3 className="text-xs font-semibold">Admin-approved claims</h3>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                  <span>{approved.data?.count ?? 0} approved; {selected.length} selected</span>
                  <button className="btn" type="button" disabled={!approved.data?.results.length}
                    onClick={() => setSelected(approved.data!.results.map((claim) => claim.id))}>Select all approved</button>
                  <button className="btn" type="button" disabled={!selected.length}
                    onClick={() => setSelected([])}>Clear</button>
                </div>
                {mappingSummary.data && <p role="status" className="mt-1 text-xs text-muted">
                  {mappingSummary.data.terms} terms, {mappingSummary.data.matched} matched, {mappingSummary.data.new} new.
                </p>}
                {approved.isLoading && <p className="text-xs text-muted">Loading claims…</p>}
                {approved.isError && <ErrorBox error={approved.error} />}
                <div className="max-h-72 overflow-y-auto">{approved.data?.results.map((claim) => <label key={claim.id} className="mt-1 flex gap-2 text-xs">
                  <Checkbox checked={selected.includes(claim.id)} onChange={() => setSelected(selected.includes(claim.id) ? selected.filter((id) => id !== claim.id) : [...selected, claim.id])} />
                  <span>{claim.normalized_claim.name ?? claim.kind} <span className="text-muted">· {claim.source_locator}</span></span>
                </label>)}</div>
                {approved.data?.count === 0 && <p className="text-xs text-muted">Approve claims in Catalog first.</p>}
              </div>
              <Button variant="primary" disabled={busy || !selected.length || (!active && !name.trim())} onClick={saveVersion}>{active ? "Save new version" : "Save as term set"}</Button>
            </>}
            {activeTab === "settings" && <p className="text-xs text-muted">The name and approved versions remain immutable. Create a new version to change terms or tags.</p>}
            {activeTab === "settings" && active && canAdmin && <div className="border-t border-border pt-3">
              <label className="text-xs">Type “{active.name}” to soft delete<input className="input mt-1" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
              <Button variant="danger" className="mt-2" disabled={busy || confirm !== active.name} onClick={remove}>Delete from library</Button>
            </div>}
          </section>
        </div>
      </div>
      {message && <p role="status" className="text-xs text-good">{message}</p>}
      {error && <ErrorBox error={error} />}
    </>}
  </div>;
}
